import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

// Diverse image categories with proper attribution
const imageCategories = {
  nature: [
    { id: 'nature-1', url: 'https://images.unsplash.com/photo-1501854140801-50d01698950b', alt: 'Mountain landscape' },
    { id: 'nature-2', url: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d', alt: 'Forest with sunlight' },
    { id: 'nature-3', url: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05', alt: 'Foggy mountain' },
    { id: 'nature-4', url: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07', alt: 'Flowers in field' }
  ],
  animals: [
    { id: 'animals-1', url: 'https://images.unsplash.com/photo-1474511320723-9a56873867b5', alt: 'Fox in nature' },
    { id: 'animals-2', url: 'https://images.unsplash.com/photo-1456926631375-92c8ce872def', alt: 'Lion closeup' },
    { id: 'animals-3', url: 'https://images.unsplash.com/photo-1425082661705-1834bfd09dca', alt: 'Penguins on ice' },
    { id: 'animals-4', url: 'https://images.unsplash.com/photo-1452570053594-1b985d6ea890', alt: 'Colorful parrot' }
  ],
  architecture: [
    { id: 'arch-1', url: 'https://images.unsplash.com/photo-1496564203457-11bb12075d90', alt: 'Modern building' },
    { id: 'arch-2', url: 'https://images.unsplash.com/photo-1487958449943-2429e8be8625', alt: 'City skyline' },
    { id: 'arch-3', url: 'https://images.unsplash.com/photo-1479839672679-a46483c0e7c8', alt: 'Bridge at night' },
    { id: 'arch-4', url: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb', alt: 'Interior design' }
  ],
  food: [
    { id: 'food-1', url: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061', alt: 'Pasta dish' },
    { id: 'food-2', url: 'https://images.unsplash.com/photo-1484723091739-30a097e8f929', alt: 'Fresh breakfast' },
    { id: 'food-3', url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836', alt: 'Various foods' },
    { id: 'food-4', url: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c', alt: 'Healthy meal' }
  ],
  travel: [
    { id: 'travel-1', url: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29', alt: 'Beach vacation' },
    { id: 'travel-2', url: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21', alt: 'Desert landscape' },
    { id: 'travel-3', url: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800', alt: 'Road trip' },
    { id: 'travel-4', url: 'https://images.unsplash.com/photo-1508672019048-805c876b67e2', alt: 'Camping scene' }
  ],
  sports: [
    { id: 'sports-1', url: 'https://images.unsplash.com/photo-1543351611-58f69d7c1781', alt: 'Person running' },
    { id: 'sports-2', url: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018', alt: 'Soccer game' },
    { id: 'sports-3', url: 'https://images.unsplash.com/photo-1517649763962-0c623066013b', alt: 'Basketball player' },
    { id: 'sports-4', url: 'https://images.unsplash.com/photo-1519861531473-9200262188bf', alt: 'Surfing wave' }
  ]
};

const AuthApp = () => {
  // State management
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [stage, setStage] = useState(0);
  const [authSelections, setAuthSelections] = useState({
    level1: null,
    level2: null,
    level3: null,
    level3Type: null // 'drawing' or 'sequence'
  });
  const [confirmSelections, setConfirmSelections] = useState({
    level1: null,
    level2: null,
    level3: null,
    level3Type: null
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [imageSequence, setImageSequence] = useState([]);
  const [confirmImageSequence, setConfirmImageSequence] = useState([]);

  // Console logging for debugging
  useEffect(() => {
    console.log('[AuthApp] Current state:', {
      mode,
      stage,
      authSelections,
      confirmSelections,
      selectedCategory,
      isLoading,
      drawingLength: drawing.length,
      imageSequenceLength: imageSequence.length
    });
  }, [mode, stage, authSelections, confirmSelections, selectedCategory, isLoading, drawing, imageSequence]);

  // Clear messages after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setError('');
      setSuccessMessage('');
    }, 5000);
    return () => clearTimeout(timer);
  }, [error, successMessage]);

  // Form validation
  const validateForm = () => {
    //console.log('[validateForm] Validating form data:', formData);
    if (!formData.email) {
      setError('Email is required');
      //console.error('[validateForm] Email missing');
      return false;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      //console.error('[validateForm] Invalid email format');
      return false;
    }

    if (mode === 'signup') {
      if (!formData.name) {
        setError('Name is required');
        //console.error('[validateForm] Name missing');
        return false;
      }
      
      if (!formData.phone) {
        setError('Phone number is required');
        //console.error('[validateForm] Phone missing');
        return false;
      }
      
      if (!formData.password) {
        setError('Password is required');
        //console.error('[validateForm] Password missing');
        return false;
      }
      
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        //console.error('[validateForm] Password too short');
        return false;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        //console.error('[validateForm] Passwords do not match');
        return false;
      }
    }
    //console.log('[validateForm] Form validation passed');
    return true;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    //console.log('[handleInputChange] Updated form field:', { [name]: value });
  };

  // Handle image selection for authentication
  const handleImageSelect = (image, level, category) => {
    //console.log('[handleImageSelect] Image selected:', { image, level, category });
    if (level === 1) {
      setAuthSelections(prev => ({ ...prev, level1: image }));
      setSelectedCategory(category); // Set category based on selected image
      //console.log('[handleImageSelect] Set selectedCategory to:', category);
    } else if (level === 3 && authSelections.level3Type === 'sequence') {
      setImageSequence(prev => [...prev, image]);
    }
    setStage(prev => prev + 1);
  };

  // Handle region selection (level 2)
  const handleRegionSelect = (region) => {
    //console.log('[handleRegionSelect] Region selected:', region);
    setAuthSelections(prev => ({ ...prev, level2: region }));
    setStage(prev => prev + 1);
  };

  // Handle region confirmation
  const handleConfirmRegionSelect = (region) => {
    //console.log('[handleConfirmRegionSelect] Region confirmed:', region);
    setConfirmSelections(prev => ({ ...prev, level2: region }));
  };

  // Validate region with tolerance
  const validateRegion = (region1, region2) => {
    if (!region1 || !region2) {
      //console.error('[validateRegion] Missing region data:', { region1, region2 });
      return false;
    }
    const tolerance = 5; // Allow ±5% difference in x and y
    const dx = Math.abs(region1.x - region2.x);
    const dy = Math.abs(region1.y - region2.y);
    const isValid = dx <= tolerance && dy <= tolerance;
    /*//console.log('[validateRegion] Region validation:', {
      region1,
      region2,
      dx,
      dy,
      isValid
    });*/
    return isValid;
  };

  // Drawing validation
  const validateDrawing = (drawing1, drawing2) => {
    if (drawing1.length !== drawing2.length || drawing1.length < 3) {
      /*//console.error('[validateDrawing] Drawing length mismatch or too short:', {
        length1: drawing1.length,
        length2: drawing2.length
      });*/
      return false;
    }

    const tolerance = 10; // Pixel tolerance for drawing comparison
    return drawing1.every((point, i) => {
      const dx = Math.abs(point.x - drawing2[i].x);
      const dy = Math.abs(point.y - drawing2[i].y);
      const isValid = dx <= tolerance && dy <= tolerance;
      /*if (!isValid) {
        //console.warn('[validateDrawing] Point mismatch:', { point, comparePoint: drawing2[i], dx, dy });
      }*/
      return isValid;
    });
  };

  // Handle canvas drawing
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDrawing(true);
    setDrawing(prev => [...prev, { x, y }]);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3;
    //console.log('[startDrawing] Drawing started at:', { x, y });
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setDrawing(prev => [...prev, { x, y }]);
    
    ctx.lineTo(x, y);
    ctx.stroke();
    //console.log('[draw] Drawing point added:', { x, y });
  };

  const endDrawing = () => {
    setIsDrawing(false);
    //console.log('[endDrawing] Drawing completed:', { points: drawing.length });
  };

  // Clear canvas
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawing([]);
    //console.log('[clearCanvas] Canvas cleared');
  };

  // Handle image sequence completion
  const completeImageSequence = () => {
    if (imageSequence.length < 3) {
      setError('Please select at least 3 images for the sequence');
      //console.error('[completeImageSequence] Insufficient images selected:', imageSequence.length);
      return;
    }
    setAuthSelections(prev => ({ ...prev, level3: imageSequence }));
    setStage(4);
    //console.log('[completeImageSequence] Sequence completed:', imageSequence);
  };

  
const handleSubmit = async (e) => {
  if (e) e.preventDefault();
  
  //console.log('[handleSubmit] Form submission initiated:', { stage, mode });
  
  if (stage === 0) {
    if (!validateForm()) return;
    setStage(1);
    return;
  }
  
  if (stage === 4) {
    /*//console.log('[handleSubmit] Verifying selections:', {
      authSelections,
      confirmSelections
    });//*/

    // Verify confirmation selections match
    let isValid = true;
    if (confirmSelections.level1?.id !== authSelections.level1?.id) {
      setError('Primary image does not match');
      isValid = false;
      //console.error('[handleSubmit] Primary image mismatch');
    }
    if (!validateRegion(confirmSelections.level2, authSelections.level2)) {
      setError('Selected region does not match');
      isValid = false;
      //console.error('[handleSubmit] Region mismatch');
    }
    if (authSelections.level3Type === 'drawing' && 
        !validateDrawing(authSelections.level3 || [], confirmSelections.level3 || [])) {
      setError('Drawing pattern does not match');
      isValid = false;
      //console.error('[handleSubmit] Drawing mismatch');
    }
    if (authSelections.level3Type === 'sequence' && 
        JSON.stringify(confirmSelections.level3?.map(img => img.id)) !== 
        JSON.stringify(authSelections.level3?.map(img => img.id))) {
      setError('Image sequence does not match');
      isValid = false;
      //console.error('[handleSubmit] Sequence mismatch');
    }

    if (!isValid) {
      // Do not reset stage or selections, allow user to retry
      return;
    }
  }
  
  setIsLoading(true);
  setError('');
  setSuccessMessage('');
  
  try {
    const endpoint = mode === 'signup' ? '/api/auth/signup' : '/api/auth/login';
    const payload = {
      email: formData.email,
      ...(mode === 'signup' && {
        name: formData.name,
        phone: formData.phone,
        password: formData.password
      }),
      authData: {
        level1: authSelections.level1,
        level2: authSelections.level2,
        level3: authSelections.level3,
        level3Type: authSelections.level3Type
      }
    };
    
    //console.log('[handleSubmit] Sending request to:', endpoint, payload);
    const response = await axios.post(endpoint, payload);
    
    // Handle success differently based on mode
    if (mode === 'signup') {
      setSuccessMessage('Account created successfully! Redirecting to login...');
      // Redirect to login page after 2 seconds
      setTimeout(() => {
        setMode('login');
        setStage(0);
        resetFormState();
      }, 2000);
    } else {
      setSuccessMessage('Login successful! Redirecting...');
      // Redirect to metaflyse page after 2 seconds
      setTimeout(() => {
        window.location.href = '/metaflyse';
      }, 2000);
    }
    
    //console.log('[handleSubmit] Authentication success:', response.data);
    
  } catch (error) {
    //console.error('[handleSubmit] Authentication error:', error);
    setError(error.response?.data?.message || 'Authentication failed. Please try again.');
    
    // For login failures, reset to stage 0 (first login step)
    if (mode === 'login') {
      setTimeout(() => {
        setStage(0);
        resetFormState();
      }, 2000);
    }
  } finally {
    setIsLoading(false);
  }
};

// Helper function to reset form state
const resetFormState = () => {
  setFormData({
    email: '',
    name: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  setAuthSelections({
    level1: null,
    level2: null,
    level3: null,
    level3Type: null
  });
  setConfirmSelections({
    level1: null,
    level2: null,
    level3: null,
    level3Type: null
  });
  setImageSequence([]);
  setConfirmImageSequence([]);
  setSelectedCategory(null);
};

  // Render current stage
  const renderStage = () => {
    switch (stage) {
      case 0: // Form stage
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-center text-gray-100">
              {mode === 'login' ? 'Welcome Back' : 'Create Your Account'}
            </h2>
            
            {error && (
              <div className="bg-red-500 text-white p-3 rounded-lg text-center flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-white hover:text-gray-200">&times;</button>
              </div>
            )}
            {successMessage && (
              <div className="bg-green-500 text-white p-3 rounded-lg text-center flex items-center justify-between">
                <span>{successMessage}</span>
                <button onClick={() => setSuccessMessage('')} className="text-white hover:text-gray-200">&times;</button>
              </div>
            )}
            
            <div className="space-y-3">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autocomplete="off"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              {mode === 'signup' && (
                <>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      autocomplete="off"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      autocomplete="off"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (123) 456-7890"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        autocomplete="off"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-white"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        autocomplete="off"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-white"
                      >
                        {showConfirmPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <button
              type="submit"
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-200 shadow-lg hover:shadow-blue-500/20"
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? 'Processing...' : 'Continue'}
            </button>
            
            <p className="text-center text-gray-400 text-sm">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => {
                  setMode(mode === 'login' ? 'signup' : 'login');
                  setFormData({
                    email: '',
                    name: '',
                    phone: '',
                    password: '',
                    confirmPassword: ''
                  });
                  console.log('[ModeSwitch] Switched mode to:', mode === 'login' ? 'signup' : 'login');
                }}
                className="text-blue-400 hover:text-blue-300 ml-1 font-medium"
              >
                {mode === 'login' ? 'Sign Up' : 'Log In'}
              </button>
            </p>
          </div>
        );
        
      case 1: // Level 1 - Image selection
        return (
          <div className="space-y-5">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-100">Level 1: Choose Your Primary Image</h2>
              <p className="text-gray-400 text-sm mt-1">
                Select an image that you'll remember. This will be your first authentication factor.
              </p>
            </div>
            
            {error && (
              <div className="bg-red-500 text-white p-3 rounded-lg text-center flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-white hover:text-gray-200">&times;</button>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(imageCategories).map(([category, images]) => (
                <div 
                  key={category}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${selectedCategory === category ? 'bg-gray-700 ring-2 ring-blue-500' : 'bg-gray-800 hover:bg-gray-700'}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                    <h3 className="text-gray-200 font-medium capitalize">{category}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {images.map((img) => (
                      <img
                        key={img.id}
                        src={img.url}
                        alt={img.alt}
                        className={`w-full h-16 object-cover rounded-md transition-transform ${authSelections.level1?.id === img.id ? 'ring-2 ring-blue-500 scale-105' : 'hover:scale-105'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageSelect(img, 1, category);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-between pt-2">
              <button
                type="button"
                onClick={() => setStage(0)}
                className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
              >
                Back
              </button>
              {authSelections.level1 && (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-300 text-sm">Selected:</span>
                  <img 
                    src={authSelections.level1.url} 
                    alt="Selected" 
                    className="w-8 h-8 object-cover rounded-md border border-gray-600"
                  />
                </div>
              )}
            </div>
          </div>
        );
        
      case 2: // Level 2 - Region selection
        return (
          <div className="space-y-5">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-100">Level 2: Select a Region</h2>
              <p className="text-gray-400 text-sm mt-1">
                Click on a specific area of your selected image. This will be your second authentication factor.
              </p>
            </div>
            
            {error && (
              <div className="bg-red-500 text-white p-3 rounded-lg text-center flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-white hover:text-gray-200">&times;</button>
              </div>
            )}
            
            <div className="relative bg-gray-800 rounded-lg overflow-hidden">
              <img
                src={authSelections.level1.url}
                alt="Selected for region"
                className="w-full h-64 object-contain bg-gray-900"
                onClick={(e) => {
                  const rect = e.target.getBoundingClientRect();
                  const x = Math.floor(((e.clientX - rect.left) / rect.width) * 100);
                  const y = Math.floor(((e.clientY - rect.top) / rect.height) * 100);
                  handleRegionSelect({ x, y });
                }}
              />
              <div className="absolute inset-0 grid grid-cols-10 grid-rows-10 pointer-events-none">
                {Array.from({ length: 100 }).map((_, i) => (
                  <div key={i} className="border border-gray-700 opacity-10"></div>
                ))}
              </div>
            </div>
            
            {authSelections.level2 && (
              <div className="text-center text-gray-300 text-sm">
                Selected region: X: {authSelections.level2.x}%, Y: {authSelections.level2.y}%
              </div>
            )}
            
            <div className="flex justify-between pt-2">
              <button
                type="button"
                onClick={() => setStage(1)}
                className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStage(3)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-lg transition disabled:opacity-50"
                disabled={!authSelections.level2}
              >
                Continue
              </button>
            </div>
          </div>
        );
        
      case 3: // Level 3 - Drawing or Sequence selection
        return (
          <div className="space-y-5">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-100">Level 3: Choose Your Third Factor</h2>
              <p className="text-gray-400 text-sm mt-1">
                Select either a drawing pattern or an image sequence as your third authentication factor.
              </p>
            </div>
            
            {error && (
              <div className="bg-red-500 text-white p-3 rounded-lg text-center flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-white hover:text-gray-200">&times;</button>
              </div>
            )}
            
           {!authSelections.level3Type ? (
            <div className="space-y-4"> 
                <div className="flex justify-center space-x-4"> 
                <button
                    type="button"
                    onClick={() => {
                    setAuthSelections(prev => ({ ...prev, level3Type: 'drawing' }));
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                    Draw Pattern
                </button>
                <button
                    type="button"
                    onClick={() => {
                    setAuthSelections(prev => ({ ...prev, level3Type: 'sequence' }));
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                    Image Sequence
                </button>
                </div>

                
                <div className="flex justify-between pt-4"> 
                <button
                    type="button"
                    onClick={() => setStage(2)} 
                    className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
                >
                    Back
                </button>
                <button
                    type="button"
                    onClick={() => setStage(3)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-lg transition disabled:opacity-50"
                    disabled={!authSelections.level2}
                >
                    Continue
                </button>
                </div>
            </div>
            ): authSelections.level3Type === 'drawing' ? (
              <div className="space-y-4">
                <h3 className="text-gray-300 font-medium">Draw Your Signature Pattern</h3>
                <div className="bg-gray-800 rounded-lg p-4">
                  <canvas
                    ref={canvasRef}
                    width={300}
                    height={200}
                    className="w-full h-48 bg-gray-900 rounded-md border border-gray-700 touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={() => {
                      endDrawing();
                      setAuthSelections(prev => ({ ...prev, level3: drawing }));
                    }}
                    onMouseLeave={endDrawing}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      startDrawing(e.touches[0]);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      draw(e.touches[0]);
                    }}
                    onTouchEnd={() => {
                      endDrawing();
                      setAuthSelections(prev => ({ ...prev, level3: drawing }));
                    }}
                  />
                </div>
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      clearCanvas();
                      setStage(2);
                    }}
                    className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={clearCanvas}
                    className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (drawing.length > 2) {
                        setStage(4);
                        console.log('[Drawing] Proceeding to confirmation');
                      } else {
                        setError('Please draw a pattern with at least 3 points');
                        console.error('[Drawing] Pattern too short');
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-lg transition"
                  >
                    Continue
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-gray-300 font-medium">Select Image Sequence (Min 3)</h3>
                <div className="grid grid-cols-2 gap-4">
                  {imageCategories[selectedCategory]?.map((img) => (
                    <img
                      key={img.id}
                      src={img.url}
                      alt={img.alt}
                      className={`w-full h-20 object-cover rounded-md cursor-pointer transition ${imageSequence.some(i => i.id === img.id) ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-500'}`}
                      onClick={() => handleImageSelect(img, 3, selectedCategory)}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {imageSequence.map((img, index) => (
                    <img
                      key={`${img.id}-${index}`}
                      src={img.url}
                      alt="Selected sequence"
                      className="w-12 h-12 object-cover rounded-md border border-gray-600"
                    />
                  ))}
                </div>
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setImageSequence([]);
                      setAuthSelections(prev => ({ ...prev, level3Type: null }));
                    }}
                    className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={completeImageSequence}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-lg transition"
                  >
                    Complete Sequence
                  </button>
                </div>
              </div>
            )}
          </div>
        );
        
      case 4: // Confirmation stage
        return (
            <div className="space-y-5">
            <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-100">Confirm Your Authentication Factors</h2>
                <p className="text-gray-400 text-sm mt-1">
                Please repeat your selections to confirm your authentication setup.
                </p>
            </div>
            
            {/* Safe back button implementation */}
            <button
                type="button"
                onClick={() => {
                // Reset confirmation selections
                setConfirmSelections({
                    level1: null,
                    level2: null,
                    level3: null,
                    level3Type: null
                });
                // Clear any drawings/sequences
                if (canvasRef.current) {
                    const ctx = canvasRef.current.getContext('2d');
                    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                }
                setDrawing([]);
                setConfirmImageSequence([]);
                // Go back to step 3
                setStage(3);
                }}
                className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
            >
                Back to Level 3
            </button>

            {error && (
                <div className="bg-red-500 text-white p-3 rounded-lg text-center flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-white hover:text-gray-200">&times;</button>
                </div>
            )}
            {successMessage && (
                <div className="bg-green-500 text-white p-3 rounded-lg text-center flex items-center justify-between">
                <span>{successMessage}</span>
                <button onClick={() => setSuccessMessage('')} className="text-white hover:text-gray-200">&times;</button>
                </div>
            )}
            
            {!confirmSelections.level1 ? (
              <div className="space-y-4">
                <h3 className="text-gray-300 font-medium">1. Confirm Your Primary Image</h3>
                <div className="grid grid-cols-2 gap-4">
                  {imageCategories[selectedCategory]?.map((img) => (
                    <img
                      key={img.id}
                      src={img.url}
                      alt={img.alt}
                      className={`w-full h-20 object-cover rounded-md cursor-pointer transition ${confirmSelections.level1?.id === img.id ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-500'}`}
                      onClick={() => {
                        setConfirmSelections(prev => ({ ...prev, level1: img }));
                        console.log('[Confirm] Primary image confirmed:', img);
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : !confirmSelections.level2 ? (
              <div className="space-y-4">
                <h3 className="text-gray-300 font-medium">2. Confirm Your Selected Region</h3>
                <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                  <img
                    src={confirmSelections.level1.url}
                    alt="Confirm region"
                    className="w-full h-64 object-contain bg-gray-900"
                    onClick={(e) => {
                      const rect = e.target.getBoundingClientRect();
                      const x = Math.floor(((e.clientX - rect.left) / rect.width) * 100);
                      const y = Math.floor(((e.clientY - rect.top) / rect.height) * 100);
                      handleConfirmRegionSelect({ x, y });
                    }}
                  />
                  <div className="absolute inset-0 grid grid-cols-10 grid-rows-10 pointer-events-none">
                    {Array.from({ length: 100 }).map((_, i) => (
                      <div key={i} className="border border-gray-700 opacity-10"></div>
                    ))}
                  </div>
                </div>
                {confirmSelections.level2 && (
                  <div className="text-center text-gray-300 text-sm">
                    Selected region: X: {confirmSelections.level2.x}%, Y: {confirmSelections.level2.y}%
                  </div>
                )}
                {confirmSelections.level2 && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleSubmit()}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-lg transition"
                    >
                      Continue
                    </button>
                  </div>
                )}
              </div>
            ) : !confirmSelections.level3 ? (
              <div className="space-y-4">
                <h3 className="text-gray-300 font-medium">3. Confirm Your {authSelections.level3Type === 'drawing' ? 'Signature Pattern' : 'Image Sequence'}</h3>
                {authSelections.level3Type === 'drawing' ? (
                  <div className="bg-gray-800 rounded-lg p-4">
                    <canvas
                      ref={canvasRef}
                      width={300}
                      height={200}
                      className="w-full h-48 bg-gray-900 rounded-md border border-gray-700 touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={() => {
                        endDrawing();
                        setConfirmSelections(prev => ({ ...prev, level3: drawing }));
                      }}
                      onMouseLeave={endDrawing}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        startDrawing(e.touches[0]);
                      }}
                      onTouchMove={(e) => {
                        e.preventDefault();
                        draw(e.touches[0]);
                      }}
                      onTouchEnd={() => {
                        endDrawing();
                        setConfirmSelections(prev => ({ ...prev, level3: drawing }));
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {imageCategories[selectedCategory]?.map((img) => (
                        <img
                          key={img.id}
                          src={img.url}
                          alt={img.alt}
                          className={`w-full h-20 object-cover rounded-md cursor-pointer transition ${confirmImageSequence.some(i => i.id === img.id) ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-500'}`}
                          onClick={() => {
                            setConfirmImageSequence(prev => [...prev, img]);
                            console.log('[Confirm] Sequence image added:', img);
                          }}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {confirmImageSequence.map((img, index) => (
                        <img
                          key={`${img.id}-${index}`}
                          src={img.url}
                          alt="Confirm sequence"
                          className="w-12 h-12 object-cover rounded-md border border-gray-600"
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={clearCanvas}
                    className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (authSelections.level3Type === 'drawing' && drawing.length > 2) {
                        setConfirmSelections(prev => ({ ...prev, level3: drawing }));
                        handleSubmit();
                      } else if (authSelections.level3Type === 'sequence' && confirmImageSequence.length >= 3) {
                        setConfirmSelections(prev => ({ ...prev, level3: confirmImageSequence }));
                        handleSubmit();
                      } else {
                        setError(`Please ${authSelections.level3Type === 'drawing' ? 'draw a pattern with at least 3 points' : 'select at least 3 images'}`);
                        console.error('[Confirm] Insufficient confirmation data');
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-lg transition"
                  >
                    {isLoading ? 'Processing...' : 'Complete'}
                  </button>
                </div>
              </div>
            ) : null}
            
            {confirmSelections.level1 && !confirmSelections.level3 && (
              <button
                type="button"
                onClick={() => {
                  if (confirmSelections.level2) {
                    setConfirmSelections(prev => ({ ...prev, level2: null }));
                  } else {
                    setConfirmSelections(prev => ({ ...prev, level1: null }));
                  }
                  console.log('[Confirm] Back to previous confirmation step');
                }}
                className="text-gray-400 hover:text-white px-3 py-1 rounded-lg transition"
              >
                Back
              </button>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
<div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-gray-900 to-gray-800 box-border">
      <div className="bg-gray-900 w-full max-w-xl p-12 rounded-2xl shadow-[0_0_30px_rgba(59,130,246,0.6)] border-2 border-gradient-to-r from-blue-500 to-purple-500">
        <div className="relative w-full h-32 overflow-hidden rounded-xl shadow-lg mb-10">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="absolute inset-0 bg-black/30 z-10 flex flex-col items-center justify-center"
            >
                <motion.img
                src="https://www.onelogin.com/blog/wp-content/uploads/2023/05/advanced-authentication-blog-image.jpg.optimal.jpg"
                alt="SecureAuth Background"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.2 }}
                />
                
                <div className="relative z-20 text-center p-2 backdrop-blur-sm bg-black/30 rounded-xl">
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                >
                    <h1 className="text-4xl font-bold text-white mb-4 tracking-wider">
                    <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                        TriSec GraphAuth
                    </span>
                    </h1>
                </motion.div>
                
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                >
                    <p className="text-xl text-gray-100 font-light mb-6">
                    Your gateway to unbreakable security
                    </p>
                </motion.div>               
                
                </div>
            </motion.div>
            </div>
        {/* Progress indicator */}
        <div className="flex justify-center mb-6 space-x-2">
          {[0, 1, 2, 3, ...(mode === 'signup' ? [4] : [])].map((i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all ${stage > i ? 'bg-blue-500' : stage === i ? 'bg-blue-400' : 'bg-gray-600'}`}
            />
          ))}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {renderStage()}
        </form>
      </div>
    </div>
  );
};

export default AuthApp;
